import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import FileInputField from "./shared/FileInputField";
import LocationPicker from "./shared/LocationPicker";
import {
  PAN_REGEX,
  fileNameFromPath,
  loadLogoPreviewWithAuthToSetter,
  parseApiError,
  matchCityName,
} from "./shared/onboardingUtils";
import State, { getStateCode } from "../../state";
import { APP_ROUTES } from "../../constants/approutes";

const CATEGORY_TYPES = ["PRIVATE", "GOVERNMENT", "TRUST"];

const BUSINESS_STATUS = {
  PENDING: 0,
  APPROVED: 1,
  RESUBMIT: 2,
  REJECTED: 3,
};

const getBusinessStatus = (data) => {
  const value = data?.businessApprovalStatus ?? data?.businessStatus;
  return value === undefined || value === null ? BUSINESS_STATUS.PENDING : Number(value);
};

const getFinanceStatus = (data) => {
  const value = data?.financeApprovalStatus ?? data?.financeStatus;
  return value === undefined || value === null ? BUSINESS_STATUS.PENDING : Number(value);
};

const getOverallStatus = (data) => {
  const businessStatus = getBusinessStatus(data);
  return businessStatus === BUSINESS_STATUS.APPROVED ? getFinanceStatus(data) : businessStatus;
};

const INTEGER_FIELDS = ["numberOfDoctorsEmpaneled", "numberOfSpecialistsEmpaneled"];
const DECIMAL_FIELDS = [];

// Backend requires these as Double; users may type "5%" etc. so pull out the numeric part on submit.
const extractDecimal = (value) => {
  const cleaned = String(value ?? "").replace(/[^0-9.]/g, "");
  if (cleaned === "" || cleaned === ".") return undefined;
  const num = Number(cleaned);
  return Number.isNaN(num) ? undefined : num;
};

const emptyForm = () => ({
  firstName: "",
  email: "",
  mobile: "",
  role: "ADMIN",
  categoryType: "HOSPI360",
  address: "",
  city: "",
  state: "",
  country: "IND",
  pinCode: "",
  latitude: "",
  longitude: "",
  pharmaName: "",
  drugLicenceNo: "",
  gst: "",
  pancard: "",
  rohiniId: "",
  numberOfDoctorsEmpaneled: "",
  numberOfSpecialistsEmpaneled: "",
  differentServicesIPD: "",
  ipdCommercialCash: "",
  ipdCommercialInsurance: "",
  ipdCommercialGovernment: "",
  healthcareLoanCommercial: "",
  paymentReconciliationOrCreditDays: "",
  referralCommissionPercentage: "",
  nonReferralCommissionPercentage: "",
  opdCommissionPercentage: "",
  agreementDate: "",
  terminationNoticePeriod: "",
  paymentPeriod: "",
  hospitalTimings: "",
  onboardingType: "Type 1",
  aboutHospital: "",
  bankName: "",
  bankAccountName: "",
  bankAccountNumber: "",
  bankIfsc: "",
  bankLocation: "",
});

const emptyErrors = () => ({
  firstName: "",
  email: "",
  mobile: "",
  categoryType: "",
  address: "",
  city: "",
  state: "",
  country: "",
  pinCode: "",
  pharmaName: "",
  drugLicenceNo: "",
  gst: "",
  pancard: "",
  hospitalLogo: "",
  pancardPhoto: "",
  certificateDoc: "",
  incorporationCertificate: "",
  location: "",
  differentServicesIPD: "",
});

const hasHospitalData = (data) =>
  Boolean(
    data &&
      (data.firstName ||
        data.name ||
        data.hospitalName ||
        data.address ||
        data.email ||
        data.userId ||
        data.hospitalId ||
        data.id)
  );

const mapHospitalToForm = (data) => ({
  firstName: data.firstName || data.name || data.hospitalName || "",
  email: data.email || "",
  mobile: data.mobile || data.mobileNumber || data.phoneNumber || "",
  role: data.role || "ADMIN",
  categoryType: data.categoryType || "HOSPI360",
  address: data.address || "",
  city: data.city || "",
  state: getStateCode(data.state || ""),
  country:  "India",
  pinCode: data.pinCode || "",
  latitude: data.latitude ?? "",
  longitude: data.longitude ?? "",
  pharmaName: data.pharmaName || "",
  drugLicenceNo: data.drugLicenceNo || "",
  gst: data.gst || "",
  pancard: data.pancard || "",
  rohiniId: data.rohiniId || "",
  numberOfDoctorsEmpaneled: data.numberOfDoctorsEmpaneled ?? "",
  numberOfSpecialistsEmpaneled: data.numberOfSpecialistsEmpaneled ?? "",
  differentServicesIPD: Array.isArray(data.differentServicesIPD)
    ? data.differentServicesIPD.join(", ")
    : data.differentServicesIPD || "",
  ipdCommercialCash: data.ipdCommercialCash ?? "",
  ipdCommercialInsurance: data.ipdCommercialInsurance ?? "",
  ipdCommercialGovernment: data.ipdCommercialGovernment ?? "",
  healthcareLoanCommercial: data.healthcareLoanCommercial ?? "",
  paymentReconciliationOrCreditDays: data.paymentReconciliationOrCreditDays || "",
  referralCommissionPercentage: data.referralCommissionPercentage ?? "",
  nonReferralCommissionPercentage: data.nonReferralCommissionPercentage ?? "",
  opdCommissionPercentage: data.opdCommissionPercentage ?? "",
  agreementDate: data.agreementDate || "",
  terminationNoticePeriod: data.terminationNoticePeriod || "",
  paymentPeriod: data.paymentPeriod || "",
  hospitalTimings: data.hospitalTimings || "",
  onboardingType: data.onboardingType || "Type 1",
  aboutHospital: data.aboutHospital || "",
  bankName: data.bankName || "",
  bankAccountName: data.bankAccountName || "",
  bankAccountNumber: data.bankAccountNumber || "",
  bankIfsc: data.bankIfsc || "",
  bankLocation: data.bankLocation || "",
});

const getHospitalLogoPath = (data) =>
  data?.hospitalLogoUrl ||
  data?.hospitalLogo ||
  data?.logoUrl ||
  data?.logourl ||
  "";

const getPancardPhotoPath = (data) =>
  data?.pancardPhotoUrl ||
  data?.pancardPhoto ||
  data?.panPhoto ||
  data?.panCardPhoto ||
  "";

const getCertificateDocPath = (data) =>
  data?.certificateDocUrl ||
  data?.certificateDoc ||
  "";

const getIncorporationCertificatePath = (data) =>
  data?.agreementAttachmentUrl ||
  data?.agreementAttachment ||
  "";

export default function HospitalOnboardingForm() {
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL;
  const role = localStorage.getItem("role") || "";
  const roleList = role.split(",").map((r) => r.trim()).filter(Boolean);
  const isBusinessRole = roleList.includes("BUSINESS");

  const [formValues, setFormValues] = useState(emptyForm());
  const [errors, setErrors] = useState(emptyErrors());
  const [hospitalLogo, setHospitalLogo] = useState(null);
  const [hospitalLogoPreview, setHospitalLogoPreview] = useState("");
  const [pancardPhoto, setPancardPhoto] = useState(null);
  const [pancardPhotoPreview, setPancardPhotoPreview] = useState("");
  const [certificateDoc, setCertificateDoc] = useState(null);
  const [certificateDocPreview, setCertificateDocPreview] = useState("");
  const [incorporationCertificate, setIncorporationCertificate] = useState(null);
  const [incorporationCertificatePreview, setIncorporationCertificatePreview] = useState("");
  const [cityOptions, setCityOptions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const lastFetchedCityStateRef = useRef("");
  // City name derived from a picked map location, applied once the city list for its
  // state finishes loading (the city dropdown is populated from a separate API by state).
  const pendingCityRef = useRef("");
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);
  const [alreadyExists, setAlreadyExists] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const needsResubmission = alreadyExists && getOverallStatus(existingData) === BUSINESS_STATUS.RESUBMIT;
  const isApproved = alreadyExists && getOverallStatus(existingData) === BUSINESS_STATUS.APPROVED;
  const formEnabled = !alreadyExists || isEditMode || needsResubmission;

  useEffect(() => {
    const stateCode = formValues.state;
    // Also refetch if the city list is empty even though this state was "already fetched" —
    // e.g. the form was reset (Cancel/after submit) back to the same state without clearing
    // this ref, which would otherwise leave the city dropdown permanently empty.
    if (!stateCode || (lastFetchedCityStateRef.current === stateCode && cityOptions.length > 0)) return;
    lastFetchedCityStateRef.current = stateCode;

    const fetchCities = async () => {
      setCityLoading(true);
      try {
        const accessToken = localStorage.getItem("access_token_che");
        const response = await fetch(
          `${process.env.REACT_APP_ENGAGE_URL}getStateMaster/list/City?stateCode=${encodeURIComponent(stateCode)}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            },
          }
        );
        const data = await response.json();
        const list = Array.isArray(data?.data) ? data.data : [];
        setCityOptions(list);
        if (pendingCityRef.current) {
          const match = matchCityName(list, pendingCityRef.current);
          if (match) setFormValues((prev) => ({ ...prev, city: match }));
          else console.warn(`No city-master match for "${pendingCityRef.current}"`);
          pendingCityRef.current = "";
        }
      } catch (error) {
        console.error("Error fetching cities by state:", error);
        setCityOptions([]);
        lastFetchedCityStateRef.current = "";
      } finally {
        setCityLoading(false);
      }
    };

    fetchCities();
  }, [formValues.state]);

  // Auto-fills State/City/Pincode from the picked location — never touches the free-text
  // Address field, which stays entirely user-entered.
  const handleLocationDetails = (details) => {
    if (!details) return;
    const stateCode = details.state ? getStateCode(details.state) : "";
    if (details.city) pendingCityRef.current = details.city;

    setFormValues((prev) => {
      const next = { ...prev };
      if (stateCode && stateCode !== prev.state) {
        next.state = stateCode;
        next.city = "";
      }
      if (details.pincode) next.pinCode = details.pincode;
      return next;
    });

    // State didn't actually change, so the city-fetch effect won't refire — try matching
    // against whatever city list is already loaded for the current state.
    if (stateCode && stateCode === formValues.state && pendingCityRef.current) {
      const match = matchCityName(cityOptions, pendingCityRef.current);
      if (match) {
        setFormValues((prev) => ({ ...prev, city: match }));
        pendingCityRef.current = "";
      } else {
        console.warn(`No city-master match for "${pendingCityRef.current}"`);
      }
    }
  };

  const loadHospitalLogoPreview = (fileName) =>
    loadLogoPreviewWithAuthToSetter(fileNameFromPath(fileName), setHospitalLogoPreview, API_BASE);

  const loadPancardPreview = (fileName) =>
    loadLogoPreviewWithAuthToSetter(fileNameFromPath(fileName), setPancardPhotoPreview, API_BASE);

  const loadCertificateDocPreview = (fileName) =>
    loadLogoPreviewWithAuthToSetter(fileNameFromPath(fileName), setCertificateDocPreview, API_BASE);

  const loadIncorporationCertificatePreview = (fileName) =>
    loadLogoPreviewWithAuthToSetter(fileNameFromPath(fileName), setIncorporationCertificatePreview, API_BASE);

  useEffect(() => {
    const fetchHospitalDetails = async () => {
      try {
        if (isBusinessRole) {
          setChecking(false);
          return;
        }

        const tenantId = localStorage.getItem("tenantId");
        const token = localStorage.getItem("auth_token");
        if (!tenantId || !token || !API_BASE) {
          setChecking(false);
          return;
        }

        const response = await fetch(`${API_BASE}hospital`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            tenantId,
            Accept: "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (hasHospitalData(data)) {
            setAlreadyExists(true);
            setExistingData(data);
            setFormValues(mapHospitalToForm(data));

            const logoPath = getHospitalLogoPath(data);
            const panPath = getPancardPhotoPath(data);
            const certPath = getCertificateDocPath(data);
            const incorporationCertPath = getIncorporationCertificatePath(data);
            if (logoPath) loadHospitalLogoPreview(logoPath);
            if (panPath) loadPancardPreview(panPath);
            if (certPath) loadCertificateDocPreview(certPath);
            if (incorporationCertPath) loadIncorporationCertificatePreview(incorporationCertPath);

            localStorage.setItem("hospitalid", data.userId || "");
            localStorage.setItem(
              "clinicId",
              data.clinicId || data.hospitalId || data.id || ""
            );
            localStorage.setItem(
              "clinilogo",
              logoPath
            );
            localStorage.setItem(
              "clinicname",
              data.name || data.hospitalName || data.firstName || ""
            );
            localStorage.setItem("organizationOnboarded", "true");
          }
        } else if (response.status !== 404) {
          console.error("Error fetching hospital details:", response.status);
        }
      } catch (error) {
        console.error("Error fetching hospital details:", error);
      } finally {
        setChecking(false);
      }
    };

    fetchHospitalDetails();
  }, [API_BASE]);

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setHospitalLogo(null);
      setHospitalLogoPreview("");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file for hospital logo");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      e.target.value = "";
      return;
    }
    setHospitalLogo(file);
    const reader = new FileReader();
    reader.onload = () => setHospitalLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handlePancardPhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setPancardPhoto(null);
      setPancardPhotoPreview("");
      return;
    }
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    if (!isImage && !isPdf) {
      toast.error("Please select an image or PDF file");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      e.target.value = "";
      return;
    }
    setPancardPhoto(file);
    if (isImage) {
      const reader = new FileReader();
      reader.onload = () => setPancardPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPancardPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleCertificateDocSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setCertificateDoc(null);
      setCertificateDocPreview("");
      return;
    }
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    if (!isImage && !isPdf) {
      toast.error("Please select an image or PDF file");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      e.target.value = "";
      return;
    }
    setCertificateDoc(file);
    if (isImage) {
      const reader = new FileReader();
      reader.onload = () => setCertificateDocPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setCertificateDocPreview(URL.createObjectURL(file));
    }
  };

  const handleIncorporationCertificateSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setIncorporationCertificate(null);
      setIncorporationCertificatePreview("");
      return;
    }
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    if (!isImage && !isPdf) {
      toast.error("Please select an image or PDF file");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      e.target.value = "";
      return;
    }
    setIncorporationCertificate(file);
    if (isImage) {
      const reader = new FileReader();
      reader.onload = () => setIncorporationCertificatePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setIncorporationCertificatePreview(URL.createObjectURL(file));
    }
  };

  const validateField = (name, value) => {
    let error = "";
    const trimmed = String(value || "").trim();
    const pancardNorm = name === "pancard" ? trimmed.toUpperCase() : trimmed;

    switch (name) {
      case "firstName":
        if (!trimmed) error = "Hospital name is required";
        break;
      case "email":
        if (!trimmed) error = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) error = "Enter a valid email";
        break;
      case "mobile":
        if (!trimmed) error = "Mobile is required";
        else if (!/^\d{10}$/.test(trimmed)) error = "Enter a valid 10-digit number";
        break;
      case "categoryType":
        if (!trimmed) error = "Category type is required";
        break;
      case "address":
        if (!trimmed) error = "Address is required";
        break;
      case "city":
        if (!trimmed) error = "City is required";
        break;
      case "state":
        if (!trimmed) error = "State is required";
        break;
      case "country":
        if (!trimmed) error = "Country is required";
        break;
      case "pinCode":
        if (!trimmed) error = "Pincode is required";
        else if (!/^\d{6}$/.test(trimmed)) error = "Enter a valid 6-digit pincode";
        break;
      case "pharmaName":
        if (!trimmed) error = "Pharmacy name is required";
        break;
      case "drugLicenceNo":
        if (!trimmed) error = "Drug licence number is required";
        break;
      case "pancard":
        if (!pancardNorm) error = "PAN card number is required";
        else if (!PAN_REGEX.test(pancardNorm)) error = "Enter a valid 10-character PAN (e.g. ABCDE1234F)";
        break;
      case "differentServicesIPD":
        if (!trimmed) error = "Different services (IPD) is required";
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let filtered = value;
    if (name === "mobile" || name === "pinCode") {
      filtered = value.replace(/\D/g, "").slice(0, name === "pinCode" ? 6 : 10);
    } else if (name === "pancard") {
      filtered = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
    } else if (INTEGER_FIELDS.includes(name)) {
      filtered = value.replace(/\D/g, "");
    } else if (DECIMAL_FIELDS.includes(name)) {
      filtered = value.replace(/[^0-9.]/g, "");
    }
    if (name === "state") {
      lastFetchedCityStateRef.current = "";
      setFormValues((prev) => ({ ...prev, state: filtered, city: "" }));
      validateField("state", filtered);
      setErrors((prev) => ({ ...prev, city: "" }));
      return;
    }
    setFormValues((prev) => ({ ...prev, [name]: filtered }));
    validateField(name, filtered);
  };

  const validateForm = () => {
    const fieldLabels = {
      firstName: "Hospital name",
      email: "Email",
      mobile: "Mobile",
      address: "Address",
      city: "City",
      state: "State",
      pinCode: "Pincode",
      pancard: "PAN card number",
      differentServicesIPD: "Different services (IPD)",
    };
    const fields = [
      "firstName",
      "email",
      "mobile",
      "address",
      "city",
      "state",
      "pinCode",
      "pancard",
      "differentServicesIPD",
    ];
    let valid = true;
    const missingFields = [];
    fields.forEach((f) => {
      if (!validateField(f, formValues[f])) {
        valid = false;
        missingFields.push(fieldLabels[f] || f);
      }
    });

    const hasExistingLogo = Boolean(getHospitalLogoPath(existingData));
    const hasExistingPan = Boolean(getPancardPhotoPath(existingData));

    let logoError = "";
    if (!hospitalLogo && !(alreadyExists && hasExistingLogo)) {
      logoError = "Hospital logo is required";
    }
    setErrors((prev) => ({ ...prev, hospitalLogo: logoError }));
    if (logoError) valid = false;

    let panPhotoError = "";
    if (!pancardPhoto && !(alreadyExists && hasExistingPan)) {
      panPhotoError = "PAN card photo is required";
    }
    setErrors((prev) => ({ ...prev, pancardPhoto: panPhotoError }));
    if (panPhotoError) valid = false;

    let locationError = "";
    if (formValues.latitude === "" || formValues.longitude === "") {
      locationError = "Location is required";
    }
    setErrors((prev) => ({ ...prev, location: locationError }));
    if (locationError) valid = false;

    if (!valid) {
      const missingDocs = [];
      if (logoError) missingDocs.push("Hospital logo");
      if (panPhotoError) missingDocs.push("PAN card photo");
      if (locationError) missingDocs.push("Location");
      const parts = [...missingFields, ...missingDocs];
      toast.error(
        parts.length
          ? `Please complete: ${parts.join(", ")}.`
          : "Please fill all required fields and upload required documents."
      );
    }

    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (alreadyExists && !isEditMode && !needsResubmission) return;
    if (!validateForm()) return;

    const tenantId = localStorage.getItem("tenantId");
    if (!tenantId) {
      toast.error("Tenant ID is missing. Please log in again.");
      return;
    }

    if (!API_BASE) {
      toast.error("API URL is not configured.");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const isUpdateFlow = alreadyExists;

      const hospitalPayload = {
        tenantId,
        ...(isUpdateFlow && {
          hospitalId: existingData?.hospitalId,
          userId: existingData?.userId,
          locationId: existingData?.locationId,
        }),
        email: formValues.email.trim(),
        mobile: formValues.mobile.trim(),
        role: formValues.role || "ADMIN",
        firstName: formValues.firstName.trim(),
        categoryType: formValues.categoryType || "PRIVATE",
        address: formValues.address.trim(),
        city: formValues.city.trim(),
        state: formValues.state.trim(),
        country: (formValues.country || "India").trim(),
        pharmaName: formValues.pharmaName.trim(),
        drugLicenceNo: formValues.drugLicenceNo.trim(),
        gst: formValues.gst.trim(),
        pancard: formValues.pancard.trim().toUpperCase(),
        pinCode: formValues.pinCode.trim(),
        latitude: formValues.latitude !== "" ? Number(formValues.latitude) : undefined,
        longitude: formValues.longitude !== "" ? Number(formValues.longitude) : undefined,
        rohiniId: formValues.rohiniId.trim(),
        numberOfDoctorsEmpaneled: formValues.numberOfDoctorsEmpaneled.trim(),
        numberOfSpecialistsEmpaneled: formValues.numberOfSpecialistsEmpaneled.trim(),
        differentServicesIPD: formValues.differentServicesIPD.trim(),
        ipdCommercialCash: formValues.ipdCommercialCash.trim(),
        ipdCommercialInsurance: formValues.ipdCommercialInsurance.trim(),
        ipdCommercialGovernment: formValues.ipdCommercialGovernment.trim(),
        healthcareLoanCommercial: formValues.healthcareLoanCommercial.trim(),
        paymentReconciliationOrCreditDays: formValues.paymentReconciliationOrCreditDays.trim(),
        referralCommissionPercentage: extractDecimal(formValues.referralCommissionPercentage),
        nonReferralCommissionPercentage: extractDecimal(formValues.nonReferralCommissionPercentage),
        opdCommissionPercentage: extractDecimal(formValues.opdCommissionPercentage),
        agreementDate: formValues.agreementDate,
        terminationNoticePeriod: formValues.terminationNoticePeriod.trim(),
        paymentPeriod: formValues.paymentPeriod.trim(),
        hospitalTimings: formValues.hospitalTimings.trim(),
        onboardingType: formValues.onboardingType.trim(),
        aboutHospital: formValues.aboutHospital.trim(),
        bankName: formValues.bankName.trim(),
        bankAccountName: formValues.bankAccountName.trim(),
        bankAccountNumber: formValues.bankAccountNumber.trim(),
        bankIfsc: formValues.bankIfsc.trim(),
        bankLocation: formValues.bankLocation.trim(),
        // ...(formValues.pinCode.trim() ? { pinCode: formValues.pinCode.trim() } : {}),
      };

      console.log(hospitalPayload, "formDatahbjk");

      const endpoint = isUpdateFlow ? "update" : "create";
      const method = isUpdateFlow ? "PUT" : "POST";

      const buildFormData = (payload) => {
        const fd = new FormData();
        fd.append("hospital", JSON.stringify(payload));
        if (hospitalLogo) fd.append("hospitalLogo", hospitalLogo, hospitalLogo.name);
        if (pancardPhoto) fd.append("pancardPhoto", pancardPhoto, pancardPhoto.name);
        if (certificateDoc) fd.append("certificateDoc", certificateDoc, certificateDoc.name);
        if (incorporationCertificate) {
          fd.append("agreementAttachment", incorporationCertificate, incorporationCertificate.name);
        }
        return fd;
      };

      const callHospitalApi = (payload, includeTenantId) =>
        fetch(`${API_BASE}hospital/${endpoint}`, {
          method,
          headers: {
            Accept: "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
            ...(includeTenantId && { tenantId }),
          },
          body: buildFormData(payload),
        });

      const res = await callHospitalApi(hospitalPayload, true);

      if (!res.ok) throw new Error(await parseApiError(res));

      const responseData = await res.json().catch(() => ({}));
      const saved = responseData && Object.keys(responseData).length ? responseData : null;

      const hasSavedIds = isUpdateFlow || saved?.userId || saved?.hospitalId || saved?.locationId;
      if (hasSavedIds) {
        try {
          const followUpPayload = isUpdateFlow
            ? hospitalPayload
            : {
                ...hospitalPayload,
                userId: saved?.userId,
                hospitalId: saved?.hospitalId,
                locationId: saved?.locationId,
              };
          await callHospitalApi(followUpPayload, false);
        } catch (followUpError) {
          console.error(`Failed to complete hospital ${endpoint} follow-up call (without tenantId):`, followUpError);
        }
      }

      localStorage.setItem("hospitalid", saved?.userId || "");
      localStorage.setItem(
        "clinicId",
        saved?.clinicId || saved?.hospitalId || saved?.id || ""
      );
      localStorage.setItem("clinilogo", getHospitalLogoPath(saved) || "");
      localStorage.setItem(
        "clinicname",
        saved?.name || saved?.hospitalName || saved?.firstName || formValues.firstName
      );
      localStorage.setItem("organizationOnboarded", "true");

      toast.success("Hospital onboarded successfully");

      if (isBusinessRole) {
        navigate(APP_ROUTES.HOSPITAL_LIST);
        return;
      }

      if (saved && hasHospitalData(saved)) {
        setAlreadyExists(true);
        setExistingData(saved);
        setFormValues(mapHospitalToForm(saved));
        setIsEditMode(false);
        const logoPath = getHospitalLogoPath(saved);
        const panPath = getPancardPhotoPath(saved);
        const certPath = getCertificateDocPath(saved);
        const incorporationCertPath = getIncorporationCertificatePath(saved);
        setHospitalLogo(null);
        setPancardPhoto(null);
        setCertificateDoc(null);
        setIncorporationCertificate(null);
        if (logoPath) loadHospitalLogoPreview(logoPath);
        else setHospitalLogoPreview("");
        if (panPath) loadPancardPreview(panPath);
        else setPancardPhotoPreview("");
        if (certPath) loadCertificateDocPreview(certPath);
        else setCertificateDocPreview("");
        if (incorporationCertPath) loadIncorporationCertificatePreview(incorporationCertPath);
        else setIncorporationCertificatePreview("");
      } else {
        setFormValues(emptyForm());
        setHospitalLogo(null);
        setHospitalLogoPreview("");
        setPancardPhoto(null);
        setPancardPhotoPreview("");
        setCertificateDoc(null);
        setCertificateDocPreview("");
        setIncorporationCertificate(null);
        setIncorporationCertificatePreview("");
      }
      setErrors(emptyErrors());
    } catch (err) {
      toast.error(err.message || "Failed to onboard hospital. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (hasError) =>
    `w-full px-3 py-2.5 border rounded-lg shadow-sm focus:outline-none focus:ring-2 ${
      hasError
        ? "border-red-500 focus:ring-red-500"
        : "border-gray-300 focus:ring-[#3cba84] focus:border-[#3cba84]"
    }`;

  const gridClass = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-5";
  const fieldDisabled = !formEnabled;

  if (checking) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004276] mx-auto mb-4" />
          <p className="text-[#004276] text-lg">Loading hospital details...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {alreadyExists && (() => {
        const businessStatus = getOverallStatus(existingData);
        const businessRemark =
          getBusinessStatus(existingData) === BUSINESS_STATUS.APPROVED
            ? existingData?.financeRemark
            : existingData?.businessRemark;

        let banner = {
          box: "bg-yellow-50 border-yellow-200",
          title: "text-yellow-800",
          text: "text-yellow-700",
          title_text: "Hospital already onboarded",
          message: "Your hospital details are saved below.",
        };

        if (businessStatus === 0) {
          banner = {
            box: "bg-amber-50 border-amber-200",
            title: "text-amber-800",
            text: "text-amber-700",
            title_text: "Submitted for approval",
            message: "Your hospital has been submitted and is pending approval.",
          };
        } else if (businessStatus === 1) {
          banner = {
            box: "bg-emerald-50 border-emerald-200",
            title: "text-emerald-800",
            text: "text-emerald-700",
            title_text: "Hospital approved",
            message: "Your hospital has been approved.",
          };
        } else if (businessStatus === 2) {
          banner = {
            box: "bg-amber-50 border-amber-200",
            title: "text-amber-800",
            text: "text-amber-700",
            title_text: "Resubmission requested",
            message: businessRemark
              ? `Please review and resubmit: ${businessRemark}`
              : "Please review and resubmit your hospital details.",
          };
        } else if (businessStatus === 3) {
          banner = {
            box: "bg-red-50 border-red-200",
            title: "text-red-800",
            text: "text-red-700",
            title_text: "Hospital rejected",
            message: businessRemark ? `Reason: ${businessRemark}` : "Your hospital submission was rejected.",
          };
        }

        return (
          <div className={`${banner.box} border rounded-lg p-4 mb-6`}>
            <h3 className={`text-sm font-medium ${banner.title}`}>{banner.title_text}</h3>
            <p className={`text-sm ${banner.text} mt-1`}>{banner.message}</p>
          </div>
        );
      })()}

      {!alreadyExists && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            No hospital profile found. Please fill in the details below to onboard your hospital.
          </p>
        </div>
      )}

    <form onSubmit={handleSubmit} noValidate>
      <fieldset disabled={fieldDisabled} className={fieldDisabled ? "opacity-90" : ""}>
      <div className="space-y-6">
        <section className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-base font-semibold text-[#004276] mb-5">Hospital details</h2>
          <div className={gridClass}>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-firstName">
                Hospital name *
              </label>
              <input
                type="text"
                id="h-firstName"
                name="firstName"
                placeholder="e.g. City Hospital"
                className={inputClass(errors.firstName)}
                value={formValues.firstName}
                onChange={handleChange}
              />
              {errors.firstName && <p className="mt-1.5 text-sm text-red-600">{errors.firstName}</p>}
            </div>
           
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-gst">
                GST
              </label>
              <input
                type="text"
                id="h-gst"
                name="gst"
                placeholder="e.g. 27AABCU9603R1ZX"
                className={inputClass(false)}
                value={formValues.gst}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-pancard">
                PAN card number *
              </label>
              <input
                type="text"
                id="h-pancard"
                name="pancard"
                placeholder="e.g. ABCDE1234F"
                maxLength={10}
                className={`${inputClass(errors.pancard)} uppercase`}
                value={formValues.pancard}
                onChange={handleChange}
              />
              {errors.pancard && <p className="mt-1.5 text-sm text-red-600">{errors.pancard}</p>}
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-rohiniId">
                Rohini ID
              </label>
              <input
                type="text"
                id="h-rohiniId"
                name="rohiniId"
                placeholder="Enter Rohini ID"
                className={inputClass(false)}
                value={formValues.rohiniId}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-mobile">
                Mobile *
              </label>
              <input
                type="tel"
                id="h-mobile"
                name="mobile"
                placeholder="10-digit mobile"
                maxLength="10"
                className={inputClass(errors.mobile)}
                value={formValues.mobile}
                onChange={handleChange}
              />
              {errors.mobile && <p className="mt-1.5 text-sm text-red-600">{errors.mobile}</p>}
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-email">
                Email *
              </label>
              <input
                type="email"
                id="h-email"
                name="email"
                placeholder="Enter email"
                className={inputClass(errors.email)}
                value={formValues.email}
                onChange={handleChange}
              />
              {errors.email && <p className="mt-1.5 text-sm text-red-600">{errors.email}</p>}
            </div>
            <FileInputField
              id="h-hospitalLogo"
              label="Hospital logo *"
              className="sm:col-span-2 lg:col-span-3"
              file={hospitalLogo}
              existingFileName={fileNameFromPath(getHospitalLogoPath(existingData))}
              previewUrl={hospitalLogoPreview}
              accept="image/*,.png,.jpg,.jpeg"
              disabled={fieldDisabled}
              error={errors.hospitalLogo}
              onChange={handleLogoSelect}
              onDelete={() => {
                setHospitalLogo(null);
                setHospitalLogoPreview("");
              }}
            />
            <FileInputField
              id="h-pancardPhoto"
              label="PAN card photo (Image/PDF) *"
              className="sm:col-span-2 lg:col-span-3"
              file={pancardPhoto}
              existingFileName={fileNameFromPath(getPancardPhotoPath(existingData))}
              previewUrl={pancardPhotoPreview}
              disabled={fieldDisabled}
              error={errors.pancardPhoto}
              onChange={handlePancardPhotoSelect}
              onDelete={() => {
                setPancardPhoto(null);
                setPancardPhotoPreview("");
              }}
            />
            <FileInputField
              id="h-certificateDoc"
              label="Certificate document (Image/PDF)"
              className="sm:col-span-2 lg:col-span-3"
              file={certificateDoc}
              existingFileName={fileNameFromPath(getCertificateDocPath(existingData))}
              previewUrl={certificateDocPreview}
              disabled={fieldDisabled}
              error={errors.certificateDoc}
              onChange={handleCertificateDocSelect}
              onDelete={() => {
                setCertificateDoc(null);
                setCertificateDocPreview("");
              }}
            />
          </div>
        </section>

        {/* <section className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-base font-semibold text-[#004276] mb-5">Pharmacy details</h2>
          <div className={gridClass}>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-pharmaName">
                Pharmacy name *
              </label>
              <input
                type="text"
                id="h-pharmaName"
                name="pharmaName"
                placeholder="e.g. City Pharma Pvt Ltd"
                className={inputClass(errors.pharmaName)}
                value={formValues.pharmaName}
                onChange={handleChange}
              />
              {errors.pharmaName && (
                <p className="mt-1.5 text-sm text-red-600">{errors.pharmaName}</p>
              )}
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-drugLicenceNo">
                Drug licence number *
              </label>
              <input
                type="text"
                id="h-drugLicenceNo"
                name="drugLicenceNo"
                placeholder="e.g. MH20PHARMACY1234567"
                className={inputClass(errors.drugLicenceNo)}
                value={formValues.drugLicenceNo}
                onChange={handleChange}
              />
              {errors.drugLicenceNo && (
                <p className="mt-1.5 text-sm text-red-600">{errors.drugLicenceNo}</p>
              )}
            </div>
          </div>
        </section> */}

        <section className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-base font-semibold text-[#004276] mb-5">Address</h2>
          <div className={gridClass}>
            <div className="lg:col-span-3 sm:col-span-2 col-span-1">
              <label className="block text-gray-700 font-medium mb-2">Location *</label>
              <LocationPicker
                latitude={formValues.latitude}
                longitude={formValues.longitude}
                disabled={fieldDisabled}
                onChange={(lat, lng) => {
                  setFormValues((prev) => ({ ...prev, latitude: lat, longitude: lng }));
                  setErrors((prev) => ({ ...prev, location: "" }));
                }}
                onLocationDetails={handleLocationDetails}
              />
              {errors.location && <p className="mt-1.5 text-sm text-red-600">{errors.location}</p>}
            </div>

            <div className="lg:col-span-3 sm:col-span-2 col-span-1">
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-address">
                Address *
              </label>
              <input
                type="text"
                id="h-address"
                name="address"
                placeholder="Enter full address"
                className={inputClass(errors.address)}
                value={formValues.address}
                onChange={handleChange}
              />
              {errors.address && <p className="mt-1.5 text-sm text-red-600">{errors.address}</p>}
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-state">
                State *
              </label>
              <State
                id="h-state"
                name="state"
                value={formValues.state}
                onChange={handleChange}
                disabled={fieldDisabled}
                className={inputClass(errors.state)}
              />
              {errors.state && <p className="mt-1.5 text-sm text-red-600">{errors.state}</p>}
            </div>
             <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-city">
                City *
              </label>
              <select
                id="h-city"
                name="city"
                className={inputClass(errors.city)}
                value={formValues.city}
                onChange={handleChange}
                disabled={fieldDisabled || cityLoading || !formValues.state}
              >
                <option value="">
                  {cityLoading
                    ? "Loading cities..."
                    : !formValues.state
                    ? "Select state first"
                    : "Select city"}
                </option>
                {cityOptions.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
              {errors.city && <p className="mt-1.5 text-sm text-red-600">{errors.city}</p>}
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-pinCode">
                Pincode *
              </label>
              <input
                type="tel"
                id="h-pinCode"
                name="pinCode"
                placeholder="6-digit pincode"
                maxLength="6"
                className={inputClass(errors.pinCode)}
                value={formValues.pinCode}
                onChange={handleChange}
              />
              {errors.pinCode && <p className="mt-1.5 text-sm text-red-600">{errors.pinCode}</p>}
            </div>
            {/* <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-country">
                Country *
              </label>
              <input
                type="text"
                id="h-country"
                name="country"
                placeholder="e.g. India"
                className={inputClass(errors.country)}
                value={formValues.country}
                onChange={handleChange}
              />
              {errors.country && <p className="mt-1.5 text-sm text-red-600">{errors.country}</p>}
            </div> */}
          </div>
        </section>

        <section className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-base font-semibold text-[#004276] mb-5">Capacity &amp; services</h2>
          <div className={gridClass}>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-numberOfDoctorsEmpaneled">
                Number of doctors empaneled
              </label>
              <input
                type="text"
                inputMode="numeric"
                id="h-numberOfDoctorsEmpaneled"
                name="numberOfDoctorsEmpaneled"
                placeholder="e.g. 12"
                className={inputClass(false)}
                value={formValues.numberOfDoctorsEmpaneled}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-numberOfSpecialistsEmpaneled">
                Number of specialists empaneled
              </label>
              <input
                type="text"
                inputMode="numeric"
                id="h-numberOfSpecialistsEmpaneled"
                name="numberOfSpecialistsEmpaneled"
                placeholder="e.g. 5"
                className={inputClass(false)}
                value={formValues.numberOfSpecialistsEmpaneled}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-differentServicesIPD">
                Different services (IPD) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="h-differentServicesIPD"
                name="differentServicesIPD"
                placeholder="e.g. Eye, Non Eye, Multi-speciality"
                className={inputClass(Boolean(errors.differentServicesIPD))}
                value={formValues.differentServicesIPD}
                onChange={handleChange}
              />
              {errors.differentServicesIPD && (
                <p className="text-red-500 text-sm mt-1">{errors.differentServicesIPD}</p>
              )}
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-hospitalTimings">
                Hospital timings
              </label>
              <input
                type="text"
                id="h-hospitalTimings"
                name="hospitalTimings"
                placeholder="e.g. 9 AM - 9 PM"
                className={inputClass(false)}
                value={formValues.hospitalTimings}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-onboardingType">
                Onboarding type
              </label>
              <select
                id="h-onboardingType"
                name="onboardingType"
                className={inputClass(false)}
                value={formValues.onboardingType}
                onChange={handleChange}
              >
                <option value="Type 1">Type 1</option>
                <option value="Type 2">Type 2</option>
              </select>
            </div>
            <div className="lg:col-span-3 sm:col-span-2 col-span-1">
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-aboutHospital">
                About hospital
              </label>
              <textarea
                id="h-aboutHospital"
                name="aboutHospital"
                rows={3}
                placeholder="Brief description about the hospital"
                className={inputClass(false)}
                value={formValues.aboutHospital}
                onChange={handleChange}
              />
            </div>
          </div>
        </section>

        <section className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-base font-semibold text-[#004276] mb-5">Commercial &amp; commission details</h2>
          <div className={gridClass}>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-ipdCommercialCash">
                IPD commercial (cash)
              </label>
              <input
                type="text"
                id="h-ipdCommercialCash"
                name="ipdCommercialCash"
                placeholder="e.g. 10000"
                className={inputClass(false)}
                value={formValues.ipdCommercialCash}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-ipdCommercialInsurance">
                IPD commercial (insurance)
              </label>
              <input
                type="text"
                id="h-ipdCommercialInsurance"
                name="ipdCommercialInsurance"
                placeholder="e.g. 12000"
                className={inputClass(false)}
                value={formValues.ipdCommercialInsurance}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-ipdCommercialGovernment">
                IPD commercial (government)
              </label>
              <input
                type="text"
                id="h-ipdCommercialGovernment"
                name="ipdCommercialGovernment"
                placeholder="e.g. 8000"
                className={inputClass(false)}
                value={formValues.ipdCommercialGovernment}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-healthcareLoanCommercial">
                Healthcare loan commercial
              </label>
              <input
                type="text"
                id="h-healthcareLoanCommercial"
                name="healthcareLoanCommercial"
                placeholder="e.g. 9000"
                className={inputClass(false)}
                value={formValues.healthcareLoanCommercial}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-paymentReconciliationOrCreditDays">
                Payment reconciliation / credit days
              </label>
              <input
                type="text"
                id="h-paymentReconciliationOrCreditDays"
                name="paymentReconciliationOrCreditDays"
                placeholder="e.g. 30 days"
                className={inputClass(false)}
                value={formValues.paymentReconciliationOrCreditDays}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-referralCommissionPercentage">
                Referral commission (%)
              </label>
              <input
                type="text"
                id="h-referralCommissionPercentage"
                name="referralCommissionPercentage"
                placeholder="e.g. 10.5"
                className={inputClass(false)}
                value={formValues.referralCommissionPercentage}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-nonReferralCommissionPercentage">
                Non-referral commission (%)
              </label>
              <input
                type="text"
                id="h-nonReferralCommissionPercentage"
                name="nonReferralCommissionPercentage"
                placeholder="e.g. 5.0"
                className={inputClass(false)}
                value={formValues.nonReferralCommissionPercentage}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-opdCommissionPercentage">
                OPD commission (%)
              </label>
              <input
                type="text"
                id="h-opdCommissionPercentage"
                name="opdCommissionPercentage"
                placeholder="e.g. 8.0"
                className={inputClass(false)}
                value={formValues.opdCommissionPercentage}
                onChange={handleChange}
              />
            </div>
          </div>
        </section>

        <section className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-base font-semibold text-[#004276] mb-5">Agreement details</h2>
          <div className={gridClass}>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-agreementDate">
                Agreement date
              </label>
              <input
                type="date"
                id="h-agreementDate"
                name="agreementDate"
                className={inputClass(false)}
                value={formValues.agreementDate}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-terminationNoticePeriod">
                Termination notice period
              </label>
              <input
                type="text"
                id="h-terminationNoticePeriod"
                name="terminationNoticePeriod"
                placeholder="e.g. 60 days"
                className={inputClass(false)}
                value={formValues.terminationNoticePeriod}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-paymentPeriod">
                Payment period
              </label>
              <input
                type="text"
                id="h-paymentPeriod"
                name="paymentPeriod"
                placeholder="e.g. Monthly"
                className={inputClass(false)}
                value={formValues.paymentPeriod}
                onChange={handleChange}
              />
            </div>
            <FileInputField
              id="h-incorporationCertificate"
              label="Certificate of Incorporation/ Partnership Deed"
              className="sm:col-span-2 lg:col-span-3"
              file={incorporationCertificate}
              existingFileName={fileNameFromPath(getIncorporationCertificatePath(existingData))}
              previewUrl={incorporationCertificatePreview}
              disabled={fieldDisabled}
              error={errors.incorporationCertificate}
              onChange={handleIncorporationCertificateSelect}
              onDelete={() => {
                setIncorporationCertificate(null);
                setIncorporationCertificatePreview("");
              }}
            />
          </div>
        </section>

        <section className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-base font-semibold text-[#004276] mb-5">Bank details</h2>
          <div className={gridClass}>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-bankName">
                Bank name
              </label>
              <input
                type="text"
                id="h-bankName"
                name="bankName"
                placeholder="e.g. HDFC Bank"
                className={inputClass(false)}
                value={formValues.bankName}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-bankAccountName">
                Bank account name
              </label>
              <input
                type="text"
                id="h-bankAccountName"
                name="bankAccountName"
                placeholder="e.g. Maayo Hospital"
                className={inputClass(false)}
                value={formValues.bankAccountName}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-bankAccountNumber">
                Bank account number
              </label>
              <input
                type="text"
                id="h-bankAccountNumber"
                name="bankAccountNumber"
                placeholder="e.g. 50100123456789"
                className={inputClass(false)}
                value={formValues.bankAccountNumber}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-bankIfsc">
                Bank IFSC
              </label>
              <input
                type="text"
                id="h-bankIfsc"
                name="bankIfsc"
                placeholder="e.g. HDFC0001234"
                className={inputClass(false)}
                value={formValues.bankIfsc}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2" htmlFor="h-bankLocation">
                Bank location
              </label>
              <input
                type="text"
                id="h-bankLocation"
                name="bankLocation"
                placeholder="e.g. Cuttack"
                className={inputClass(false)}
                value={formValues.bankLocation}
                onChange={handleChange}
              />
            </div>
          </div>
        </section>
      </div>
      </fieldset>

      <div className="mt-8 border-t border-gray-200 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
        {alreadyExists ? (
          isEditMode || needsResubmission ? (
            <>
              {isEditMode && (
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    if (existingData) {
                      setFormValues(mapHospitalToForm(existingData));
                      const logoPath = getHospitalLogoPath(existingData);
                      const panPath = getPancardPhotoPath(existingData);
                      const certPath = getCertificateDocPath(existingData);
                      const incorporationCertPath = getIncorporationCertificatePath(existingData);
                      setHospitalLogo(null);
                      setPancardPhoto(null);
                      setCertificateDoc(null);
                      setIncorporationCertificate(null);
                      if (logoPath) loadHospitalLogoPreview(logoPath);
                      else setHospitalLogoPreview("");
                      if (panPath) loadPancardPreview(panPath);
                      else setPancardPhotoPreview("");
                      if (certPath) loadCertificateDocPreview(certPath);
                      else setCertificateDocPreview("");
                      if (incorporationCertPath) loadIncorporationCertificatePreview(incorporationCertPath);
                      else setIncorporationCertificatePreview("");
                    }
                    setIsEditMode(false);
                    setErrors(emptyErrors());
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className={`px-5 py-2.5 bg-gradient-to-r from-[#004172] to-[#3cba84] text-white rounded-lg ${
                  submitting ? "opacity-70 cursor-not-allowed" : "hover:opacity-90"
                }`}
                disabled={submitting}
              >
                {submitting ? "Processing..." : needsResubmission ? "Resubmit Hospital" : "Update Hospital"}
              </button>
            </>
          ) : isApproved ? (
            <button
              type="button"
              className="px-5 py-2.5 bg-[#004276] text-white rounded-lg hover:opacity-90"
              onClick={() => setIsEditMode(true)}
            >
              Edit hospital details
            </button>
          ) : (
            ""
          )
        ) : (
          <>
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              onClick={() => {
                setFormValues(emptyForm());
                setHospitalLogo(null);
                setHospitalLogoPreview("");
                setPancardPhoto(null);
                setPancardPhotoPreview("");
                setCertificateDoc(null);
                setCertificateDocPreview("");
                setIncorporationCertificate(null);
                setIncorporationCertificatePreview("");
                setErrors(emptyErrors());
              }}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-5 py-2.5 bg-gradient-to-r from-[#004172] to-[#3cba84] text-white rounded-lg ${
                submitting ? "opacity-70 cursor-not-allowed" : "hover:opacity-90"
              }`}
              disabled={submitting}
            >
              {submitting ? "Processing..." : "Onboard Hospital"}
            </button>
          </>
        )}
      </div>
    </form>
    </>
  );
}
